import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import DynamicFieldForm from "@/components/DynamicFieldForm";
import { CONTENT } from "@/constants/contentTestIds";

/**
 * Single component handles both create (when itemId is undefined) and edit.
 * Routes:
 *   /brands/:brandId/content/:categoryId/items/new
 *   /brands/:brandId/content-items/:itemId
 */
export default function ContentItemEditor() {
  const params = useParams();
  const navigate = useNavigate();
  const isEdit = !!params.itemId;

  const [cat, setCat] = useState(null);
  const [item, setItem] = useState(null);
  const [name, setName] = useState("");
  const [values, setValues] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [catalogCampaigns, setCatalogCampaigns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      if (isEdit) {
        const { data: it } = await api.get(`/brands/${params.brandId}/content-items/${params.itemId}`);
        setItem(it);
        setName(it.name);
        setValues(it.values || {});
        setCampaigns(it.campaigns || []);
        const { data: c } = await api.get(`/brands/${params.brandId}/content-categories/${it.category_id}`);
        setCat(c);
      } else {
        const { data: c } = await api.get(`/brands/${params.brandId}/content-categories/${params.categoryId}`);
        setCat(c);
      }
      try {
        const { data: cat } = await api.get(`/brands/${params.brandId}/campaign-catalog`);
        setCatalogCampaigns(cat.campaigns || []);
      } catch { /* non-fatal: chip selector hides if catalog is unreachable */ }
    })();
  }, [params.itemId, params.categoryId]);

  const isPromotion = cat?.slug === "promotion";

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = { name, values, campaigns: isPromotion ? campaigns : [] };
      if (isEdit) {
        await api.put(`/brands/${params.brandId}/content-items/${params.itemId}`, payload);
        navigate(`/brands/${params.brandId}/content/${item.category_id}`);
      } else {
        const { data } = await api.post(
          `/brands/${params.brandId}/content-categories/${params.categoryId}/items`,
          payload,
        );
        navigate(`/brands/${params.brandId}/content-items/${data.item_id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/brands/${params.brandId}/content-items/${params.itemId}`);
    navigate(`/brands/${params.brandId}/content/${item.category_id}`);
  };

  if (!cat) return <p className="text-[#6B6358]">Loading&hellip;</p>;

  return (
    <div data-testid={CONTENT.itemRoot} className="max-w-4xl">
      <p className="text-xs uppercase tracking-[0.22em] text-[#B5462F] mb-2">
        {cat.name} &middot; {isEdit ? "Edit item" : "New item"}
      </p>
      <h1 className="font-serif text-4xl tracking-tight mb-8">
        {isEdit ? name : "New item"}
      </h1>

      <div className="mb-8">
        <label className="block text-[11px] uppercase tracking-[0.18em] text-[#6B6358] mb-1.5">
          Item name <span className="text-[#B5462F]">*</span>
          <span className="ml-2 normal-case tracking-normal text-[#6B6358] italic font-mono text-[10px]">
            internal label
          </span>
        </label>
        <input
          data-testid={CONTENT.itemName}
          type="text"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 border border-[#1A1F2C]/20 rounded-md bg-[#F8F5EE]"
        />
      </div>

      <h2 className="font-serif text-xl mb-4">Fields</h2>
      <div className="border border-[#1A1F2C]/10 bg-white rounded-lg p-6">
        <DynamicFieldForm
          fields={cat.fields}
          values={values}
          onChange={setValues}
          brandId={params.brandId}
          testidPrefix={CONTENT.itemFieldPrefix}
        />
      </div>

      {isPromotion && (
        <div data-testid="content-item-campaigns-section" className="mt-8">
          <h2 className="font-serif text-xl mb-1">Campaign tags</h2>
          <p className="text-xs text-[#6B6358] mb-4 max-w-2xl">
            Pick every Campaign this promotion should appear under in the
            Design Request form. A single promotion can be tagged with
            multiple campaigns. Untagged promotions stay hidden from the
            marketer dropdown.
          </p>
          <div className="flex flex-wrap gap-2">
            {catalogCampaigns.length === 0 && (
              <p className="text-[#6B6358] text-sm italic">Catalog unreachable.</p>
            )}
            {catalogCampaigns.map((c) => {
              const active = campaigns.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  data-testid={`content-item-campaign-${c.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() =>
                    setCampaigns((prev) =>
                      active ? prev.filter((x) => x !== c) : [...prev, c],
                    )
                  }
                  className={[
                    "px-3 py-1.5 rounded-full text-xs uppercase tracking-[0.15em] border",
                    active
                      ? "bg-[#1A1F2C] text-[#F8F5EE] border-[#1A1F2C]"
                      : "bg-white text-[#3D3A35] border-[#1A1F2C]/30",
                  ].join(" ")}
                >
                  {c}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {error && <p className="text-sm text-[#B5462F] mt-4">{error}</p>}

      <div className="flex items-center gap-3 mt-8 pt-6 border-t border-[#1A1F2C]/10">
        <button
          data-testid={CONTENT.itemSave}
          onClick={save}
          disabled={saving || !name}
          className="px-5 py-2 bg-[#1A1F2C] text-[#F8F5EE] rounded-full text-sm font-medium disabled:opacity-40"
        >
          {saving ? "Saving..." : "Save item"}
        </button>
        {isEdit && (
          <button
            data-testid={CONTENT.itemDelete}
            onClick={remove}
            className="ml-auto px-5 py-2 text-sm text-[#B5462F]"
          >
            Delete
          </button>
        )}
      </div>
    </div>
  );
}
